import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {getClientEnv} from '@nosible/shared';
import {Button} from '@nosible/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nosible/shared/components/ui/card';
import {NameLogoHorizontal} from '@/account/components/Logo';
import {sharedAuthRedirect} from '@/account/lib/auth-redirect';
import {
  magicLinkConfirmationUrl,
  proofFromMagicLinkFragment,
} from '@/account/lib/magic-link-confirmation';

type ConfirmationState =
  | {status: 'loading'}
  | {status: 'ready'; email: string}
  | {status: 'submitting'; email: string}
  | {status: 'success'}
  | {status: 'error'; message: string};

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

async function postConfirmation(
  operation: 'inspect' | 'consume',
  proof: string,
  signal?: AbortSignal,
): Promise<{response: Response; payload: unknown}> {
  const env = getClientEnv();
  const response = await fetch(
    magicLinkConfirmationUrl(env.API_BASE_URL, operation),
    {
      method: 'POST',
      body: JSON.stringify({proof}),
      cache: 'no-store',
      credentials: 'include',
      headers: {'content-type': 'application/json'},
      referrerPolicy: 'no-referrer',
      signal,
    },
  );
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // Stable client messages below deliberately hide proxy/internal details.
  }
  return {response, payload};
}

function confirmationError(status?: number): string {
  if (status === 429) {
    return 'Too many sign-in attempts. Please wait and request a new link.';
  }
  if (status === 400 || status === 403) {
    return 'This sign-in link is invalid, expired, or already used.';
  }
  return 'Sign-in is temporarily unavailable. Please request a new link and try again.';
}

export function MagicLinkConfirm() {
  const navigate = useNavigate();
  const [proof] = useState(() =>
    typeof window === 'undefined'
      ? null
      : proofFromMagicLinkFragment(window.location.hash),
  );
  const [state, setState] = useState<ConfirmationState>({status: 'loading'});

  useEffect(() => {
    // Remove the bearer proof from visible history before any API call or
    // third-party resource can observe a navigated URL.
    window.history.replaceState(window.history.state, '', '/confirm-login');
    if (!proof) {
      setState({
        status: 'error',
        message: 'This sign-in link is invalid. Please request a new one.',
      });
      return;
    }

    const controller = new AbortController();
    void postConfirmation('inspect', proof, controller.signal)
      .then(({response, payload}) => {
        if (
          response.ok &&
          isRecord(payload) &&
          typeof payload.email === 'string' &&
          payload.email.length > 0
        ) {
          setState({status: 'ready', email: payload.email});
          return;
        }
        setState({status: 'error', message: confirmationError(response.status)});
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setState({status: 'error', message: confirmationError()});
      });
    return () => controller.abort();
  }, [proof]);

  const handleConfirmLogin = async () => {
    if (!proof || state.status !== 'ready') return;
    const email = state.email;
    setState({status: 'submitting', email});

    try {
      const {response, payload} = await postConfirmation('consume', proof);
      if (
        !response.ok ||
        !isRecord(payload) ||
        payload.status !== true ||
        typeof payload.redirectTo !== 'string'
      ) {
        setState({status: 'error', message: confirmationError(response.status)});
        return;
      }

      // Defense in depth: the API returns a signed, server-canonicalized
      // destination, and the browser independently applies the same first-
      // party allowlist before navigating.
      const target = sharedAuthRedirect(payload.redirectTo, getClientEnv());
      setState({status: 'success'});
      window.location.replace(target);
    } catch {
      setState({status: 'error', message: confirmationError()});
    }
  };

  if (state.status === 'success') {
    return (
      <div className="auth-page">
        <Card className="auth-page__card auth-page__card--compact">
          <CardHeader className="auth-page__header">
            <NameLogoHorizontal className="auth-page__wordmark" aria-label="NOSIBLE" />
            <CardTitle className="auth-page__title">Sign-in confirmed</CardTitle>
            <CardDescription className="auth-page__description">Redirecting you now.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const email = state.status === 'ready' || state.status === 'submitting'
    ? state.email
    : null;
  const error = state.status === 'error' ? state.message : null;

  return (
    <div className="auth-page">
      <Card className="auth-page__card auth-page__card--confirm">
        <CardHeader className="auth-page__header">
          <NameLogoHorizontal className="auth-page__wordmark" aria-label="NOSIBLE" />
          <p className="auth-page__eyebrow">Secure access</p>
          <CardTitle className="auth-page__title">Confirm sign in</CardTitle>
          <CardDescription className="auth-page__description">
            {email
              ? <>Signing in as <strong>{email}</strong></>
              : state.status === 'loading'
                ? 'Checking your sign-in link.'
                : 'This sign-in could not be confirmed.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="auth-page__content auth-page__content--confirm">
          {error ? <p className="auth-page__error auth-page__error--panel" role="alert">{error}</p> : email ? <p className="auth-page__confirmation-note">Only continue if you requested this sign-in.</p> : null}
          <Button onClick={handleConfirmLogin} disabled={state.status !== 'ready'} className="auth-page__primary">
            {state.status === 'submitting' ? 'Verifying…' : 'Confirm sign in'}
          </Button>
          {error && <Button onClick={() => navigate('/login')} variant="outline" className="auth-page__secondary">Request a new sign-in link</Button>}
        </CardContent>
      </Card>
    </div>
  );
}
